import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { buttonVariants } from '@/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

export interface CalendarProps {
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  mode?: 'single' | 'range';
  fromDate?: Date;
  toDate?: Date;
  disabled?: (date: Date) => boolean;
  className?: string;
  rangeStart?: Date;
  rangeEnd?: Date;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isSameDay(date1: Date, date2: Date | undefined): boolean {
  if (!date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isDisabled(
  date: Date,
  fromDate?: Date,
  toDate?: Date,
  disabledFn?: (date: Date) => boolean,
): boolean {
  if (fromDate && date < fromDate) return true;
  if (toDate && date > toDate) return true;
  if (disabledFn && disabledFn(date)) return true;
  return false;
}

function CalendarComponent(
  {
    className,
    selected,
    onSelect,
    fromDate,
    toDate,
    disabled,
    rangeStart,
    rangeEnd,
    ...props
  }: CalendarProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { i18n } = useTranslation();
  const locale = i18n.language.startsWith('es') ? es : enUS;

  const today = new Date();
  const [currentMonth, setCurrentMonth] = React.useState(
    (rangeEnd ?? rangeStart ?? selected ?? today).getMonth(),
  );
  const [currentYear, setCurrentYear] = React.useState(
    (rangeEnd ?? rangeStart ?? selected ?? today).getFullYear(),
  );

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  // Locale-aware month and day labels
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const name = format(new Date(2024, i, 1), 'LLLL', { locale });
    return name.charAt(0).toUpperCase() + name.slice(1);
  });

  // Unbounded ends default to today ± a wide range — e.g. a hire date needs
  // to reach decades back, which plain prev/next-month arrows make tedious.
  const yearRangeStart = fromDate ? fromDate.getFullYear() : today.getFullYear() - 100;
  const yearRangeEnd = toDate ? toDate.getFullYear() : today.getFullYear() + 10;
  const yearOptions = Array.from(
    { length: yearRangeEnd - yearRangeStart + 1 },
    (_, i) => yearRangeStart + i,
  );

  const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
    // Jan 7 2024 = Sunday, Jan 8 = Monday … Jan 13 = Saturday
    const d = new Date(2024, 0, 7 + i);
    const name = format(d, 'EEE', { locale });
    return name.charAt(0).toUpperCase() + name.slice(1).replace('.', '');
  });

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    if (!onSelect) return;
    const date = new Date(currentYear, currentMonth, day);
    if (isDisabled(date, fromDate, toDate, disabled)) return;
    onSelect(date);
  };

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const inRangeMode = !!(rangeStart || rangeEnd);

  return (
    <div ref={ref} className={cn('p-3', className)} {...props}>
      <div className="mb-4 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'h-8 w-8 p-0 shrink-0')}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-1">
          <Select value={String(currentMonth)} onValueChange={(v) => setCurrentMonth(Number(v))}>
            <SelectTrigger
              size="sm"
              className="h-8 w-[6.5rem] text-xs"
              data-testid="calendar-month-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((label, index) => (
                <SelectItem key={label} value={String(index)} className="text-xs">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(currentYear)} onValueChange={(v) => setCurrentYear(Number(v))}>
            <SelectTrigger
              size="sm"
              className="h-8 w-[4.5rem] text-xs"
              data-testid="calendar-year-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {yearOptions.map((year) => (
                <SelectItem key={year} value={String(year)} className="text-xs">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          type="button"
          onClick={goToNextMonth}
          className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'h-8 w-8 p-0 shrink-0')}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-2 grid grid-cols-7">
        {daysOfWeek.map((day) => (
          <div key={day} className="py-2 text-center text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="h-9" />;
          }

          const date = new Date(currentYear, currentMonth, day);
          const isToday = isSameDay(date, todayDate);
          const isDisabledDate = isDisabled(date, fromDate, toDate, disabled);

          // Range mode
          const isRangeStartDay = inRangeMode && rangeStart && isSameDay(date, rangeStart);
          const isRangeEndDay = inRangeMode && rangeEnd && isSameDay(date, rangeEnd);
          const isSingleRangeDay = isRangeStartDay && isRangeEndDay;
          const isInRange =
            !isSingleRangeDay && rangeStart && rangeEnd && date > rangeStart && date < rangeEnd;

          // Single mode
          const isSingleSelected = !inRangeMode && selected && isSameDay(date, selected);

          const isHighlighted = isRangeStartDay || isRangeEndDay || isSingleSelected;

          return (
            <button
              key={day}
              type="button"
              onClick={() => handleDateClick(day)}
              disabled={isDisabledDate}
              className={cn(
                'h-9 w-9 rounded-md text-sm transition-colors',
                // Single selection or range endpoint
                isHighlighted && 'bg-primary text-primary-foreground hover:bg-primary/90',
                // In-range highlight
                isInRange && 'bg-primary/15 rounded-md',
                // Hover for non-selected
                !isHighlighted && !isInRange && 'hover:bg-accent',
                // Today ring
                isToday && !isHighlighted && 'ring-2 ring-ring',
                // Disabled
                isDisabledDate && 'cursor-not-allowed opacity-50 hover:bg-transparent',
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const Calendar = React.forwardRef(CalendarComponent);
Calendar.displayName = 'Calendar';
